import { useLayoutEffect, useRef, type ReactNode } from "react";
import { usePresence } from "framer-motion";
import { Ban } from "lucide-react";
import nextBiteIcon from "@/assets/next-bite.svg";
import exitLessonIcon from "@/assets/exit-lesson.svg";
import { NotificationType } from "@/store/useNotificationStore";

export const TUTOR_VIEW_NOTIFICATION_SUPPORTED_TYPES: NotificationType[] = [
  NotificationType.LOCKED,
  NotificationType.NEXT_BITE,
  NotificationType.EXIT_LESSON,
];

export const NOTIFICATION_SUPPORTED_TYPES: NotificationType[] = [
  NotificationType.SUCCESS,
  NotificationType.WARNING,
  NotificationType.DISABLED,
  NotificationType.SPACE_STUDENT,
];

const EXIT_KEYFRAMES: Keyframe[] = [
  { opacity: 1, transform: "scale(1)" },
  { opacity: 0, transform: "scale(0.95)" },
];
const EXIT_DURATION_MS = 200;

interface TutorViewNotificationProps {
  id: string;
  type: NotificationType;
  title: ReactNode;
  subheading: string;
  onClose?: () => void;
}

const TutorViewNotification = ({
  type,
  title,
  subheading,
  onClose,
}: TutorViewNotificationProps) => {
  const toastRef = useRef<HTMLDivElement>(null);
  const [isPresent, safeToRemove] = usePresence();

  // Exit through the Web Animations API, not framer-motion's `exit`: motion
  // starts it on the main thread, which the next slide's mount then blocks,
  // freezing the toast on screen. Started here, it runs on the compositor.
  useLayoutEffect(() => {
    if (isPresent) return;
    const animation = toastRef.current?.animate?.(EXIT_KEYFRAMES, {
      duration: EXIT_DURATION_MS,
      easing: "ease-out",
      fill: "forwards",
    });
    const remove = () => safeToRemove?.();
    if (!animation) {
      // Not within this commit: AnimatePresence ignores a removal it hasn't
      // registered yet.
      void Promise.resolve().then(remove);
      return;
    }
    animation.finished.then(remove, remove);
  }, [isPresent, safeToRemove]);

  const getIcon = () => {
    switch (type) {
      case NotificationType.LOCKED:
        return (
          <div className="flex-shrink-0 flex items-start justify-center">
            <Ban size={25} className="text-red-500" strokeWidth={3} />
          </div>
        );
      case NotificationType.NEXT_BITE:
        return (
          <div className="flex-shrink-0 flex items-center justify-center">
            <img src={nextBiteIcon} alt="Next Bite" width={30} height={30} />
          </div>
        );
      case NotificationType.EXIT_LESSON:
        return (
          <div className="flex-shrink-0 flex items-center justify-center">
            <img
              src={exitLessonIcon}
              alt="Exit Lesson"
              width={30}
              height={30}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    // CSS entrance, not framer-motion, for the same reason as the exit.
    <div
      ref={toastRef}
      className="animate-toast-enter rounded-[20px] bg-[#252525] shadow-lg overflow-hidden border border-[#F2F2F2]"
      style={{ width: 380 }}
    >
      <div style={{ padding: "16px 20px" }}>
        <div
          className="flex flex-col items-start justify-between w-full"
          style={{ gap: 10 }}
        >
          <div className="flex flex-row gap-4 justify-between w-full">
            <div className="flex flex-row gap-4 w-full">
              {getIcon()}
              <p
                className="text-white mb-1"
                style={{
                  fontFamily: "Nunito, sans-serif",
                  fontWeight: 800,
                  fontSize: "20px",
                  lineHeight: "150%",
                  letterSpacing: "-0.022em",
                }}
              >
                {title}
              </p>
            </div>
          </div>

          <p
            className="text-[#BEBEBE] whitespace-pre-line"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "150%",
              letterSpacing: "-0.011em",
            }}
          >
            {subheading}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TutorViewNotification;
